/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useId, useRef, useState } from 'react';
import type { FieldErrors, UseFormRegisterReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import { AlreadyLoggedInNotice } from '@/components/trial/already-logged-in-notice';
import type { AlertStateOrEmpty } from '@/components/ui/alert';
import { Alert } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { useCaptchaBypass } from '@/hooks/use-captcha-bypass';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { FORM_GAMES, V4A_NAMESPACE } from '../constants';
import { ArrowIcon, CheckCircleIcon } from './ui';

interface FormData {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

type FieldName = Exclude<keyof FormData, 'recaptcha'>;

const DEFAULT_VALUES: FormData = { name: '', email: '', repeatEmail: '', password: '', passwordRepeat: '', recaptcha: '' };
const FIELD_ORDER: readonly FieldName[] = ['name', 'email', 'repeatEmail', 'password', 'passwordRepeat'];

export interface SignupFormProps {
  trialConfigId: number | null;
  trialPageId: number | null;
  isDisabledButton?: boolean;
  /** Prefix for every id in the card (`np` hero, `nf` closing band, `nd` dialog): three cards share the page. */
  idPrefix: string;
}

const LABEL_CLASS = 'np-field__label inline-flex min-w-0 items-center gap-[.4rem] justify-self-start text-[length:var(--fs-xs)] font-semibold uppercase leading-[1.2] tracking-[var(--ls-caps)] text-[color:var(--fg-muted)] [overflow-wrap:anywhere]';
const FIELD_CLASS = 'np-field grid min-w-0 grid-rows-[auto_auto_auto] content-start gap-[var(--x-gap)]';
const INPUT_CLASS = 'np-field__input h-[var(--x-field-h)] w-full py-0 text-[color:var(--fg-strong)]';
const MSG_CLASS = 'np-field__msg min-h-0 min-w-0 items-start gap-[.35rem] ps-0.5 tracking-[.01em]';
const ROW_CLASS = 'np-form__row np-form__row--2 grid min-w-0 gap-4';

interface FieldProps {
  id: string;
  label: string;
  registration: UseFormRegisterReturn;
  error?: string;
  isValid: boolean;
  placeholder: string;
  autoComplete: string;
  inputMode?: 'email';
  type?: 'text' | 'password';
  /** A hint on the label's row (the password rule): `id` is referenced by the input's aria-describedby. */
  hint?: { id: string; text: string };
  toggle?: { show: string; hide: string };
}

function EyeOnIcon() {
  return (
    <svg className='ico-on' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg className='ico-off' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M3 3l18 18' />
      <path d='M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16.6 16.6 0 0 1-3.2 3.7' />
      <path d='M6.6 6.7A16.4 16.4 0 0 0 2.5 12S6 18 12 18a9.6 9.6 0 0 0 4.2-.9' />
      <path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
    </svg>
  );
}

/** One `.np-field`: label with its valid tick, the control (and its eye toggle), the message slot. */
function Field({ id, label, registration, error, isValid, placeholder, autoComplete, inputMode, type = 'text', hint, toggle }: FieldProps) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const messageId = `${id}-msg`;
  const labelElement = (
    <label className={classNames(LABEL_CLASS, hint && 'shrink-0')} htmlFor={id}>
      {label}
      <CheckCircleIcon className='np-field__ok' />
    </label>
  );
  const { ref: registerRef, ...inputProps } = registration;
  // The caret survives the type swap, like the source's toggle.
  const handleToggle = () => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? null;
    const end = input?.selectionEnd ?? null;
    setVisible((previous) => !previous);
    window.requestAnimationFrame(() => {
      if (input && start !== null && end !== null) {
        try {
          input.setSelectionRange(start, end);
        } catch {
          // Some input types refuse a selection range; the caret simply moves to the end.
        }
      }
    });
  };
  return (
    <div className={classNames(FIELD_CLASS, type === 'password' && 'np-field--password', error && 'np-field--invalid', !error && isValid && 'np-field--valid')}>
      {hint ? (
        <div className='np-field__top flex min-h-[calc(var(--fs-xs)_*_1.2)] min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1'>
          {labelElement}
          <span className='np-field__hint min-w-0 flex-auto text-end text-[length:var(--fs-xs)] font-medium leading-[1.2] tracking-[.01em] text-[color:var(--fg-dim)] [overflow-wrap:anywhere]' id={hint.id}>
            {hint.text}
          </span>
        </div>
      ) : (
        labelElement
      )}
      <div className='np-field__control relative min-w-0'>
        <input
          {...inputProps}
          ref={(element) => {
            registerRef(element);
            inputRef.current = element;
          }}
          className={classNames(INPUT_CLASS, toggle ? 'pe-[3.25rem] ps-3.5' : 'px-3.5')}
          id={id}
          type={type === 'password' && !visible ? 'password' : 'text'}
          inputMode={inputMode}
          maxLength={255}
          autoComplete={autoComplete}
          dir={autoComplete === 'name' ? 'auto' : undefined}
          autoCapitalize={inputMode === 'email' ? 'off' : undefined}
          spellCheck={inputMode === 'email' ? false : undefined}
          placeholder={placeholder}
          aria-required='true'
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={hint ? `${hint.id} ${messageId}` : messageId}
          data-lpignore='true'
        />
        {toggle && (
          <button className='np-field__toggle absolute end-0.5 top-1/2 inline-flex size-11 items-center justify-center' type='button' aria-label={visible ? toggle.hide : toggle.show} aria-controls={id} data-visible={visible ? 'true' : 'false'} onClick={handleToggle}>
            <EyeOnIcon />
            <EyeOffIcon />
          </button>
        )}
      </div>
      <span className={classNames(MSG_CLASS, error && 'is-error')} id={messageId}>
        {error}
      </span>
    </div>
  );
}

function countErrors(errors: FieldErrors<FormData>): number {
  return FIELD_ORDER.filter((field) => errors[field]).length;
}

/**
 * The design's enrolment card ("Teste grátis sem cartão"), wired to the same trial signup as
 * CreateAccountV2: yup rules and messages from `common:login`, captcha, SSO, `createTrial` with the
 * block's `custom` (trial config id) + `trialPageId` + UTM, then login and the no-card thank-you page.
 * A visitor already signed in gets AlreadyLoggedInNotice instead of a second account form.
 */
export function SignupForm({ trialConfigId, trialPageId, isDisabledButton = false, idPrefix }: SignupFormProps) {
  const router = useRouter();
  const auth = useAuth();
  const { t } = useTranslation([V4A_NAMESPACE, 'common']);
  const { bypassCaptcha } = useCaptchaBypass();
  const titleId = `${idPrefix}-form-title`;
  const liveId = useId();
  const [isLoading, setIsLoading] = useState(false);
  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const [popUp, setPopUp] = useState<{ show: boolean; message: string; state: AlertStateOrEmpty }>({ show: false, message: '', state: '' });
  const [liveMessage, setLiveMessage] = useState('');
  const captchaResetRef = useRef<(() => void) | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const resetCaptcha = useCallback(() => captchaResetRef.current?.(), []);

  const schema = yup.object().shape({
    name: personNameSchema(t('common:login.mensage_error_empty_name'), t('common:login.mensage_error_invalid_name')),
    email: yup.string().email(t('common:login.mensage_error_valid_email')).required(t('common:login.mensage_error_empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('common:login.mensage_error_valid_email'))
      .required(t('common:login.mensage_error_empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('common:login.mensage_error_repeat_email')),
    password: yup.string().required(t('common:login.mensage_error_empty_password')).min(6, t('common:login.mensage_error_short_password')),
    passwordRepeat: yup
      .string()
      .required(t('common:login.mensage_error_empty_password'))
      .min(6, t('common:login.mensage_error_short_password'))
      .oneOf([yup.ref('password')], t('common:login.mensage_error_repeat_password')),
    recaptcha: yup.string().required(t('common:login.mensage_error_empty_captch')),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<FormData>({ resolver: yupResolver(schema), defaultValues: DEFAULT_VALUES, mode: 'onBlur' });
  const values = watch();

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );
  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required', message: t('common:login.mensage_error_empty_captch') });
  }, [setValue, setError, t]);
  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    resetCaptcha();
  }, [setValue, resetCaptcha]);

  const handleSsoStart = useCallback(() => setIsSsoAuthenticating(true), []);
  const handleSsoSuccess = useCallback(() => setIsSsoAuthenticating(false), []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const onSubmit = async (data: FormData) => {
    if (isDisabledButton) return;
    const { email, password, passwordRepeat, recaptcha, name } = data;
    setIsLoading(true);
    setPopUp({ show: false, message: '', state: '' });
    const utmData = getUtmData();
    try {
      const body: CreateUserTrialParams = {
        email,
        password,
        password_repeat: passwordRepeat,
        recaptcha,
        ref: null,
        name,
        ...utmData,
      };
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      body.custom = trialConfigId;
      body.trialPageId = trialPageId;

      await auth.createTrial(
        { ...body },
        (error) => {
          setValue('recaptcha', '');
          resetCaptcha();
          const message = t(apiErrorMessageKey(error, { fallbackKey: 'common:login.mensage_error_create_user_generic', statusKeys: TRIAL_STATUS_KEYS }));
          setPopUp({ show: true, message, state: 'error' });
          setLiveMessage(message);
          setIsLoading(false);
        },
        () => {
          auth.login({ login: email, password, recaptcha });
          sendGTMEvent({ event: 'createAccount' });
          reset();
          sessionStorage.setItem('customTrialApproved', 'true');
          router.push('/thanks-for-trial-no-card');
          setIsLoading(false);
        },
        false
      );
    } catch {
      setValue('recaptcha', '');
      resetCaptcha();
      setPopUp({ show: true, message: t('common:login.mensage_error_create_user_generic'), state: 'error' });
      setIsLoading(false);
    }
  };

  // The first invalid field is brought clear of the sticky bar before it takes focus (WCAG 2.4.11).
  const onInvalid = (formErrors: FieldErrors<FormData>) => {
    const count = countErrors(formErrors);
    if (count) setLiveMessage(t('form.fix_summary', { count }));
    const firstField = FIELD_ORDER.find((field) => formErrors[field]);
    const input = firstField ? formRef.current?.querySelector<HTMLInputElement>(`[name="${firstField}"]`) : null;
    if (!input) return;
    const header = document.querySelector('.np-header');
    const bar = header ? header.getBoundingClientRect().height : 0;
    const row = input.closest('.np-field') ?? input;
    const box = row.getBoundingClientRect();
    if (box.top < bar + 8 || box.bottom > window.innerHeight - 8) {
      const free = window.innerHeight - bar;
      const offset = box.height + 48 < free ? bar + (free - box.height) / 2 : bar + 24;
      window.scrollTo({ top: Math.max(0, Math.round(box.top + window.scrollY - offset)), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    input.focus({ preventScroll: true });
  };

  if (auth.user) {
    return (
      <div className='np-form relative isolate w-full rounded-[var(--r-xl)] border border-solid border-[color:rgb(255_255_255_/_.10)] p-[var(--x-pad)] text-[color:var(--fg)] [--x-pad:var(--sp-6)] [box-shadow:var(--shadow-xl),_var(--shadow-inset)]'>
        <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
      </div>
    );
  }

  const errorCount = countErrors(errors);
  const showSummary = isSubmitted && errorCount > 0;
  const fieldState = (field: FieldName) => ({
    error: errors[field]?.message,
    isValid: Boolean((touchedFields[field] || isSubmitted) && values[field]?.trim()),
  });
  // A blur on an empty, untouched field says nothing; a blur after typing validates (the source's rule).
  const blurWhenTyped = (field: FieldName) => {
    const registration = register(field);
    return {
      ...registration,
      onBlur: (event: Parameters<typeof registration.onBlur>[0]) => {
        const target = event.target as HTMLInputElement;
        if (!target.value.trim() && !touchedFields[field] && !isSubmitted) return Promise.resolve();
        return registration.onBlur(event);
      },
    };
  };
  const passwordHintId = `${idPrefix}-pass-hint`;

  return (
    <form ref={formRef} className='np-form relative isolate w-full rounded-[var(--r-xl)] border border-solid border-[color:rgb(255_255_255_/_.10)] p-[var(--x-pad)] text-[color:var(--fg)] [--x-err-rgb:255_82_48] [--x-err-text:#FF8C70] [--x-err:#FF5230] [--x-field-bg:#14161B] [--x-field-border-hover:#8F98AC] [--x-field-border:#737C92] [--x-field-h:2.75rem] [--x-field-r:var(--r-md)] [--x-gap:.25rem] [--x-pad:var(--sp-6)] [box-shadow:var(--shadow-xl),_var(--shadow-inset),_0_0_0_1px_rgb(var(--accent-rgb)_/_.06),_0_0_140px_-30px_rgb(var(--accent-rgb)_/_.35)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]' id={`${idPrefix}-form`} noValidate autoComplete='on' aria-labelledby={titleId} onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      <div className='np-form__head mb-6 text-center'>
        <h2 className='np-form__title text-balance text-[length:clamp(1.375rem,_1.1rem_+_1.1vw,_1.75rem)] font-bold uppercase leading-[1.1] tracking-[-.01em] text-[color:var(--fg-strong)]' id={titleId}>
          <Trans t={t} i18nKey='form.title' components={{ hl: <span className='np-form__hl text-[color:var(--accent)] [text-shadow:0_0_10px_rgb(var(--accent-rgb)_/_.32)]' /> }} />
        </h2>
        <p className='np-form__sub mt-2 text-[length:var(--fs-sm)] leading-5 text-[color:var(--fg-muted)]'>
          {t('form.subtitle_no_card')}
          <span className='dot me-[.3em] ms-[.35em] inline font-bold text-[color:var(--accent)]' aria-hidden='true'>
            ·
          </span>
          &nbsp;{t('form.subtitle_access')}
        </p>
      </div>

      <p className='np-form__live sr-only' role='status' aria-live='polite' id={liveId}>
        {liveMessage}
      </p>

      <div className='np-form__sso mb-5'>
        <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
      </div>

      <div className='np-form__fields grid min-w-0 gap-4'>
        <Field id={`${idPrefix}-name`} label={t('form.name')} registration={blurWhenTyped('name')} placeholder={t('form.name_placeholder')} autoComplete='name' {...fieldState('name')} />
        <div className={ROW_CLASS}>
          <Field id={`${idPrefix}-email`} label={t('form.email')} registration={blurWhenTyped('email')} placeholder={t('form.email_placeholder')} autoComplete='email' inputMode='email' {...fieldState('email')} />
          <Field id={`${idPrefix}-email2`} label={t('form.repeat_email')} registration={blurWhenTyped('repeatEmail')} placeholder={t('form.repeat_email_placeholder')} autoComplete='email' inputMode='email' {...fieldState('repeatEmail')} />
        </div>
        <div className={ROW_CLASS}>
          <Field id={`${idPrefix}-pass`} label={t('form.password')} registration={blurWhenTyped('password')} placeholder={t('form.password_placeholder')} autoComplete='new-password' type='password' hint={{ id: passwordHintId, text: t('form.password_hint') }} toggle={{ show: t('form.show_password'), hide: t('form.hide_password') }} {...fieldState('password')} />
          <Field id={`${idPrefix}-pass2`} label={t('form.repeat_password')} registration={blurWhenTyped('passwordRepeat')} placeholder={t('form.repeat_password_placeholder')} autoComplete='new-password' type='password' toggle={{ show: t('form.show_password_repeat'), hide: t('form.hide_password_repeat') }} {...fieldState('passwordRepeat')} />
        </div>
      </div>

      <div className='np-form__turnstile mt-5 flex min-h-[65px] flex-col items-center justify-center'>
        <CaptchaProvider theme='dark' onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} resetRef={captchaResetRef} />
        {bypassCaptcha && (
          <div className='np-form__turnstile-ph min-h-[65px] w-full items-center gap-3 px-3.5 py-2 [box-shadow:inset_0_1px_0_rgb(255_255_255_/_.03),_inset_0_2px_6px_rgb(0_0_0_/_.18)]' data-state='ready'>
            <svg className='np-form__shield' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
              <path d='M12 3 4.5 6v5.3c0 4.6 3.1 8.7 7.5 9.7 4.4-1 7.5-5.1 7.5-9.7V6L12 3Z' />
              <path className='np-form__shield-check' d='m9.1 12.3 2 2 3.9-4.4' />
            </svg>
            <span className='np-form__turnstile-copy grid min-w-0 flex-auto gap-[.2rem]'>
              <span className='np-form__turnstile-text min-w-0 [overflow-wrap:anywhere]'>{t('form.security_title')}</span>
              <span className='np-form__turnstile-sub min-w-0 text-pretty text-[length:.8125rem] font-medium leading-[1.3] text-[color:var(--fg)] [overflow-wrap:anywhere]'>{t('form.security_ready')}</span>
            </span>
          </div>
        )}
        {errors.recaptcha && <span className='np-field__msg is-error mt-2'>{errors.recaptcha.message}</span>}
      </div>

      <div className={classNames('np-form__status np-form__status--top grid', showSummary && 'is-visible')} data-state={showSummary ? 'error' : undefined}>
        <div className='flex min-h-0 items-start gap-3 overflow-hidden text-[length:var(--fs-sm)] leading-[1.4]'>
          {showSummary && (
            <>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <circle cx='12' cy='12' r='9' />
                <path d='M12 7.8v4.9' />
                <path d='M12 16.2h.01' />
              </svg>
              <span>{t('form.fix_summary', { count: errorCount })}</span>
            </>
          )}
        </div>
      </div>

      {popUp.show && (
        <div className='mt-5'>
          <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />
        </div>
      )}

      <button className={classNames('btn btn-primary btn--block np-form__submit relative isolate mt-5 min-h-[var(--x-h)] w-full max-w-full items-center justify-center gap-2 whitespace-normal p-3 text-center uppercase tracking-[.04em] no-underline [--x-h:3.25rem]', isLoading && 'is-busy')} type='submit' disabled={isDisabledButton || isLoading} aria-busy={isLoading ? 'true' : undefined}>
        <span className='np-form__submit-label min-w-0 text-balance [overflow-wrap:anywhere]'>{isLoading ? t('form.submitting') : <Trans t={t} i18nKey='form.submit' components={{ end: <span className='np-form__submit-end inline-block whitespace-nowrap' />, arrow: <ArrowIcon className='np-form__submit-arrow' /> }} />}</span>
        <svg className='np-form__submit-spin' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
          <path d='M12 3a9 9 0 1 1-6.4 2.6' opacity='.35' />
          <path d='M12 3a9 9 0 0 1 9 9' />
        </svg>
      </button>

      <ul className='np-form__games mt-4 flex list-none flex-wrap items-center justify-center gap-x-0 gap-y-[.4rem] p-0 text-[length:var(--fs-2xs)] font-semibold uppercase tabular-nums leading-[1.4] tracking-[.08em] text-[color:var(--fg)]' role='list' aria-label={t('form.games_label')}>
        {FORM_GAMES.map((game) => (
          <li key={game} className='inline-flex items-center whitespace-nowrap text-center [overflow-wrap:normal]'>
            {game}
          </li>
        ))}
      </ul>

      <p className='np-form__legal mt-2 text-balance px-2 text-center text-[length:var(--fs-2xs)] leading-normal text-[color:var(--fg-dim)]'>
        <Trans
          t={t}
          i18nKey='form.legal'
          components={{
            terms: <Link className='relative rounded-[2px]' href='/terms/terms-of-use' target='_blank' rel='noopener' />,
            privacy: <Link className='relative rounded-[2px]' href='/terms/privacy-policy' target='_blank' rel='noopener' />,
            newtab: <span className='sr-only' />,
          }}
        />
      </p>
    </form>
  );
}
